import * as React from "react";
import Moment from "moment";
import { Avatar, Typography, WithStyles, createStyles, Theme, IconButton } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import { connect } from "react-redux";
import { GlobalState } from "./../../types/globalState";
import { Tournament } from "./../../types/tournament";
import GameActions from "../../actions/games";
import TournamentActions from "../../actions/tournaments";
import { Assignment, GolfCourse } from "@material-ui/icons";
import { showDialog } from "../SharedComponents/Dialog";
import TournamentLeaderboard from "../TournamentLeaderboard";
import TournamentGamesManager from "./TournamentGamesManager";
import TournamentTrophyIcon, { TOURNAMENT_TROPHY_COLOR } from "../SharedComponents/TournamentTrophyIcon";
import { Rules, User } from "../../types/user";

export interface TournamentHistoryProps extends WithStyles {
  user: User;
  tournaments: Tournament[];
  getTournaments: () => void;
  getTournamentGames: (tournamentId: string, callback?: () => void) => void;
}

class TournamentHistory extends React.Component<TournamentHistoryProps> {
  componentDidMount() {
    this.props.getTournaments();
  }

  handleOnTournamentClick = (tournament: Tournament) => {
    const seasonRange = `${Moment(tournament.seasonStart).format("ll")} – ${Moment(tournament.seasonEnd).format("ll")}`;

    this.props.getTournamentGames(tournament._id || "", () => {
      showDialog(
        <TournamentLeaderboard tournament={tournament} />,
        "md",
        true,
        false,
        true,
        tournament.name,
        undefined,
        undefined,
        seasonRange,
        true,
      );
    });
  };

  renderNoTournament = () => {
    const { classes } = this.props;

    return (
      <div className={classes.emptyTournament}>
        <Typography variant="body2" gutterBottom>
          You haven't joined a tournament yet.
        </Typography>
      </div>
    );
  };

  render() {
    const { classes, user } = this.props;
    return (
      <div className={classes.page}>
        {!this.props.tournaments.length
          ? this.renderNoTournament()
          : this.props.tournaments
              .slice()
              .sort((a, b) => ((b as Tournament & { createDate?: string }).createDate || "") >
                ((a as Tournament & { createDate?: string }).createDate || "") ? 1 : -1)
              .map(tournament => {
                const seasonStart = Moment(tournament.seasonStart).format("ll");
                const seasonEnd = Moment(tournament.seasonEnd).format("ll");

                return (
                  <div key={tournament._id} className={classes.row}>
                    <Avatar className={classes.avatar} aria-hidden>
                      <TournamentTrophyIcon className={classes.avatarIcon} />
                    </Avatar>

                    <div className={classes.nameBlock}>
                      <Typography variant="subtitle2" component="h3" className={classes.tournamentName} noWrap>
                        {tournament.name}
                      </Typography>
                    </div>

                    <Typography
                      variant="caption"
                      className={classes.datesMobile}
                      color="textSecondary"
                      component="p"
                    >
                      {seasonStart}
                      <span className={classes.dateSep}> – </span>
                      {seasonEnd}
                    </Typography>

                    <Typography className={classes.datesDesktop} variant="body2" component="div">
                      <span className={classes.datePrimary}>{seasonStart}</span>
                      <span className={classes.dateSecondary}>{seasonEnd}</span>
                    </Typography>

                    <div className={classes.actions}>
                      <IconButton
                        size="small"
                        aria-label="Open tournament leaderboard"
                        className={classes.actionBtn}
                        onClick={() => this.handleOnTournamentClick(tournament)}
                      >
                        <Assignment fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Manage tournament rounds"
                        className={classes.actionBtn}
                        onClick={() =>
                          showDialog(
                            <TournamentGamesManager
                              tournamentId={tournament._id}
                              tournament={tournament}
                              editable={user.rule === Rules.Admin || tournament.owner === user._id}
                            />,
                            "xl",
                            true,
                            true,
                            true,
                            `${tournament.name}`,
                          )
                        }
                      >
                        <GolfCourse fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })}
      </div>
    );
  }
}
const styles = (theme: Theme) =>
  createStyles({
    page: {
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      backgroundColor: "#ffffff",
    },
    row: {
      display: "grid",
      gridTemplateColumns: "auto 1fr auto",
      gridTemplateRows: "auto auto",
      gridTemplateAreas: `
        "avatar main actions"
        "avatar datesMobile actions"
      `,
      alignItems: "center",
      columnGap: theme.spacing(1.5),
      rowGap: theme.spacing(0.25),
      padding: theme.spacing(1.25, 1.75),
      borderBottom: `1px solid rgba(226, 232, 240, 0.95)`,
      transition: theme.transitions.create("background-color", {
        duration: theme.transitions.duration.shortest,
      }),
      "&:active": {
        backgroundColor: "rgba(248, 250, 252, 1)",
      },

      [theme.breakpoints.up("sm")]: {
        gridTemplateColumns: "auto minmax(0, 1fr) minmax(8.5rem, 9.5rem) auto",
        gridTemplateRows: "auto",
        gridTemplateAreas: `"avatar main datesDesktop actions"`,
        rowGap: theme.spacing(0),
        padding: theme.spacing(1.5, 3),
      },
    },
    avatar: {
      gridArea: "avatar",
      alignSelf: "center",
      width: 40,
      height: 40,
      backgroundColor: "rgba(226, 232, 240, 0.95)",
      border: "1px solid rgba(203, 213, 225, 0.85)",

      [theme.breakpoints.up("sm")]: {
        width: 44,
        height: 44,
      },
    },
    avatarIcon: {
      fontSize: "1.35rem",
      color: TOURNAMENT_TROPHY_COLOR,
    },
    nameBlock: {
      gridArea: "main",
      minWidth: 0,
      alignSelf: "end",

      [theme.breakpoints.up("sm")]: {
        alignSelf: "center",
      },
    },
    tournamentName: {
      fontWeight: 600,
      letterSpacing: "-0.02em",
      lineHeight: 1.35,
      color: "#0f172a",
    },
    datesMobile: {
      gridArea: "datesMobile",
      margin: 0,
      lineHeight: 1.35,

      [theme.breakpoints.up("sm")]: {
        display: "none",
      },
    },
    datesDesktop: {
      display: "none",
      gridArea: "datesDesktop",
      textAlign: "right",
      minWidth: 0,

      [theme.breakpoints.up("sm")]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: theme.spacing(0.25),
      },
    },
    datePrimary: {
      fontWeight: 600,
      color: "#334155",
      fontSize: "0.8125rem",
    },
    dateSecondary: {
      fontSize: "0.75rem",
      color: "#94a3b8",
    },
    dateSep: {
      color: "#cbd5e1",
    },
    actions: {
      gridArea: "actions",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: theme.spacing(0.25),
      flexShrink: 0,
    },
    actionBtn: {
      color: "#64748b",
      padding: theme.spacing(1),
      "&:active": {
        transform: "scale(0.96)",
      },
    },
    emptyTournament: {
      textAlign: "center",
      padding: theme.spacing(3),
      color: "#64748b",
    },
  });

const mapStateToProps = (state: GlobalState) => {
  const tournaments = state.games.tournaments;
  const { user } = state.auth;
  return {
    tournaments,
    user,
  };
};

export default connect(mapStateToProps, {
  getTournaments: GameActions.getTournaments,
  getTournamentGames: TournamentActions.getTournamentGames,
})(withStyles(styles)(TournamentHistory));
